/**
 * @deprecated This file is deprecated and will be removed in a future version.
 * Please use the new kioskApi service instead: import { products, customers, orders, payments } from '../services/kioskApi';
 * This service makes secure API calls to our backend server instead of directly to the WooCommerce API.
 * 
 * Migration guide:
 * - Replace `wooApi.searchProducts(term)` with `kioskApi.products.search(term)`
 * - Replace `wooApi.searchCustomers(term)` with `kioskApi.customers.search(term)`
 * - Replace `wooApi.createCustomer(data)` with `kioskApi.customers.create(data)`
 * - For orders and payments, use the corresponding methods in the kioskApi service
 */

// WooCommerce API Configuration
const wooConfig = {
  // Make sure this URL points to your WooCommerce REST API endpoint
  // It should end with /wp-json/wc/v3 (no trailing slash)
  //url: 'https://icctmemphis.org/wp-json/wc/v3',
  url: process.env.REACT_APP_API_BASE_URL,
  
  // IMPORTANT: Store these in environment variables in production
  // Example: process.env.REACT_APP_WOOCOMMERCE_CONSUMER_KEY
  //consumerKey: 'ck_f62f6402bfc5410a59dc30cf8633806f4032d7d3',
  //consumerSecret: 'cs_99c9269c5ec4d0d8dd039c908449e5d3c1c8685e',
  //version: 'wc/v3' // WooCommerce REST API version
  consumerKey: process.env.REACT_APP_WOOCOMMERCE_CONSUMER_KEY,
  consumerSecret: process.env.REACT_APP_WOOCOMMERCE_CONSUMER_SECRET,
  version: process.env.REACT_APP_WOOCOMMERCE_API_VERSION // WooCommerce REST API version
};

// Function to test WooCommerce API access
export const testWooCommerceAPI = async (productId) => {
  if (!productId) {
    throw new Error('No product ID provided for testing');
  }
  console.log(`Testing WooCommerce API for product ${productId}...`);
  
  try {
    const baseUrl = wooConfig.url.endsWith('/') 
      ? wooConfig.url.replace(/\/$/, '') 
      : wooConfig.url;
    
    const endpoint = `${baseUrl}/products/${productId}`;
    console.log('Fetching product details from:', endpoint);
    
    // First, get the product details
    const productResponse = await fetch(endpoint, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Basic ' + btoa(`${wooConfig.consumerKey}:${wooConfig.consumerSecret}`)
      }
    });
    
    if (!productResponse.ok) {
      const errorText = await productResponse.text();
      console.error('Failed to fetch product:', {
        status: productResponse.status,
        statusText: productResponse.statusText,
        error: errorText
      });
      return { success: false, error: 'Failed to fetch product' };
    }
    
    const product = await productResponse.json();
    console.log('Product details:', {
      id: product.id,
      name: product.name,
      type: product.type,
      hasVariations: product.variations?.length > 0,
      variationCount: product.variations?.length || 0
    });
    
    // If the product has variations, fetch them
    if (product.variations && Array.isArray(product.variations) && product.variations.length > 0) {
      console.log(`Product has ${product.variations.length} variations, fetching details...`);
      
      if (product.variations.length === 0) {
        console.log('No variations found for this product');
        return {
          success: true,
          product: {
            id: product.id,
            name: product.name,
            type: product.type,
            hasVariations: false,
            message: 'No variations found for this product'
          }
        };
      }
      
      const variationsEndpoint = `${baseUrl}/products/${productId}/variations`;
      console.log('Fetching variations from:', variationsEndpoint);
      
      const variationsResponse = await fetch(variationsEndpoint, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${wooConfig.consumerKey}:${wooConfig.consumerSecret}`)
        }
      });
      
      if (!variationsResponse.ok) {
        const errorText = await variationsResponse.text();
        console.error('Failed to fetch variations:', {
          status: variationsResponse.status,
          statusText: variationsResponse.statusText,
          error: errorText
        });
        return { 
          success: false, 
          error: 'Successfully fetched product but failed to fetch variations',
          product: {
            id: product.id,
            name: product.name,
            type: product.type,
            hasVariations: true,
            variationCount: product.variations.length
          }
        };
      }
      
      const variations = await variationsResponse.json();
      console.log(`Successfully fetched ${variations.length} variations`);
      
      // Return both product and variations
      return {
        success: true,
        product: {
          id: product.id,
          name: product.name,
          type: product.type,
          hasVariations: true,
          variationCount: variations.length,
          variations: variations.map(v => ({
            id: v.id,
            name: v.name,
            price: v.price,
            stock_status: v.stock_status,
            attributes: v.attributes || []
          }))
        }
      };
    }
    
    // For simple products, just return the product details
    return {
      success: true,
      product: {
        id: product.id,
        name: product.name,
        type: product.type,
        price: product.price,
        stock_status: product.stock_status,
        hasVariations: false
      }
    };
    
  } catch (error) {
    console.error('Error testing WooCommerce API:', error);
    return { 
      success: false, 
      error: error.message || 'An unknown error occurred',
      details: error
    };
  }
};

// Create and export the WooCommerce API client
export const wooApi = {
  // Search for existing customers
  searchCustomers: async (searchTerm = '') => {
    try {
      // Remove any trailing slashes from the base URL and add the correct path
      const baseUrl = wooConfig.url.endsWith('/') 
        ? wooConfig.url.replace(/\/$/, '') 
        : wooConfig.url;
      
      // Use /customers endpoint for search/lookup
      const endpoint = `${baseUrl}/customers`;
      
      const params = new URLSearchParams({
        context: 'view',
        search: searchTerm,
        per_page: 20 // Limit the number of results
      });

      console.log('Making API request to:', endpoint);
      console.log('With params:', params.toString());

      const response = await fetch(
        `${endpoint}?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Basic ' + btoa(`${wooConfig.consumerKey}:${wooConfig.consumerSecret}`),
            'Accept': 'application/json'
          },
          mode: 'cors',
          credentials: 'same-origin'
        }
      );

      console.log('API response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.text();
        console.error('API error response:', errorData);
        throw new Error(`Failed to fetch customers: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('API response data:', data);
      return data;
    } catch (error) {
      console.error('Error in searchCustomers:', error);
      throw new Error(`Search failed: ${error.message}`);
    }
  },
  
  // Create a new customer in WooCommerce using the kiosk-customers endpoint
  createCustomer: (customerData) => {
    if (!wooConfig.url) {
      return Promise.reject(new Error('WooCommerce API URL is not configured'));
    }
    if (!wooConfig.consumerKey || !wooConfig.consumerSecret) {
      return Promise.reject(new Error('WooCommerce API credentials are not configured'));
    }

    const baseUrl = 'https://icctmemphis.org';
    const endpoint = `${baseUrl}/wp-json/wc/v3/kiosk-customers`;
    
    // Prepare the request body
    const requestBody = {
      email: customerData.email,
      first_name: customerData.firstName,
      last_name: customerData.lastName,
      username: customerData.email,
      billing: {
        first_name: customerData.firstName,
        last_name: customerData.lastName,
        email: customerData.email,
        phone: customerData.phone || ''
      },
      shipping: {
        first_name: customerData.firstName,
        last_name: customerData.lastName
      }
    };

    // Create a new XMLHttpRequest to match Postman's behavior
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', endpoint, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Authorization', 'Basic ' + btoa(`${wooConfig.consumerKey}:${wooConfig.consumerSecret}`));
      xhr.setRequestHeader('Accept', 'application/json');
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            console.log('Customer created successfully:', response);
            resolve(response);
          } catch (e) {
            console.error('Error parsing response:', e);
            reject(new Error('Invalid response from server'));
          }
        } else {
          console.error('Request failed with status:', xhr.status);
          console.error('Response text:', xhr.responseText);
          
          let errorMessage = `Request failed with status ${xhr.status}`;
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            if (errorResponse && errorResponse.message) {
              errorMessage = errorResponse.message;
            }
          } catch (e) {
            // If we can't parse the error response, use the default message
          }
          
          reject(new Error(errorMessage));
        }
      };
      
      xhr.onerror = function() {
        console.error('Request failed');
        reject(new Error('Network error. Please check your connection and try again.'));
      };
      
      console.log('Sending request to:', endpoint);
      console.log('Request body:', JSON.stringify(requestBody, null, 2));
      xhr.send(JSON.stringify(requestBody));
    });
  },
  
  // Search for products in WooCommerce
  searchProducts: async (searchTerm = '') => {
    console.log('Starting product search with term:', searchTerm);
    try {
      if (!wooConfig.url) {
        throw new Error('WooCommerce API URL is not configured');
      }
      
      const baseUrl = wooConfig.url.endsWith('/') 
        ? wooConfig.url.replace(/\/$/, '') 
        : wooConfig.url;
      
      const endpoint = `${baseUrl}/products`;
      const params = new URLSearchParams({
        search: searchTerm,
        per_page: 20,
        status: 'publish',
        _fields: 'id,name,price,regular_price,sale_price,images,variations,categories,price_html',
        catalog_visibility: 'visible',
        stock_status: 'instock'
      });
      
      console.log('Searching products with params:', params.toString());
      
      const response = await fetch(
        `${endpoint}?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Basic ' + btoa(`${wooConfig.consumerKey}:${wooConfig.consumerSecret}`),
            'Accept': 'application/json'
          },
          // Add timeout to prevent hanging
          signal: AbortSignal.timeout(30000) // 30 seconds
        }
      );
      
      if (!response.ok) {
        const errorData = await response.text();
        console.error('Product search failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(`Failed to search products: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Products found:', data.length);
      
      // Log the first product to verify the structure
      if (data.length > 0) {
        console.log('First product sample:', {
          id: data[0].id,
          name: data[0].name,
          price: data[0].price,
          hasVariations: data[0].variations?.length > 0,
          image: data[0].images?.[0]?.src
        });
      }
      
      return data;
      
    } catch (error) {
      console.error('Error in searchProducts:', error);
      throw new Error(`Product search failed: ${error.message}`);
    }
  },
  
  // Get product variations from WooCommerce
  getProductVariations: async (productId) => {
    console.group(`[WooCommerce] getProductVariations for product ${productId}`);
    
    // Log the product being processed
    console.log(`Processing product ID ${productId} - checking for variations`);
    
    try {
      if (!productId) {
        const error = new Error('No product ID provided');
        console.error('Validation error:', error);
        throw error;
      }
      
      console.log('Building API URL...');
      const baseUrl = wooConfig.url.endsWith('/') 
        ? wooConfig.url.replace(/\/$/, '') 
        : wooConfig.url;
      const endpoint = `${baseUrl}/products/${productId}/variations`;
      
      console.log('API Endpoint:', endpoint);
      const ck = wooConfig.consumerKey || '';
      console.log('Using consumer key:', ck ? `${ck.substring(0, 5)}...${ck.slice(-5)}` : '[not set]');
      
      console.log('Sending request to WooCommerce API...');
      const startTime = Date.now();
      
      const fetchOptions = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${wooConfig.consumerKey}:${wooConfig.consumerSecret}`),
          'Accept': 'application/json'
        },
        // Add timeout and other fetch options if needed
        signal: AbortSignal.timeout(10000) // 10 second timeout
      };
      
      console.log('Fetching variations with options:', {
        endpoint,
        method: fetchOptions.method,
        headers: {
          ...fetchOptions.headers,
          // Don't log full auth key for security
          'Authorization': 'Basic [REDACTED]'
        }
      });
      
      const response = await fetch(endpoint, fetchOptions);
      
      const responseTime = Date.now() - startTime;
      console.log(`Response received in ${responseTime}ms`);
      console.log('Response status:', response.status, response.statusText);
      
      const responseClone = response.clone(); // Clone response for error handling
      
      if (!response.ok) {
        let errorData;
        let errorText;
        try {
          errorText = await responseClone.text();
          try {
            errorData = JSON.parse(errorText);
            console.error('Error response from API (JSON):', errorData);
          } catch (jsonError) {
            console.error('Failed to parse error response as JSON, raw response:', errorText);
            errorData = { message: errorText };
          }
        } catch (e) {
          console.error('Failed to read error response:', e);
          errorData = { message: 'Failed to read error response' };
        }
        
        const error = new Error(
          `Failed to fetch variations: ${response.status} ${response.statusText}`
        );
        error.status = response.status;
        error.response = errorData;
        console.error('API Error:', {
          message: error.message,
          status: error.status,
          statusText: response.statusText,
          response: error.response
        });
        throw error;
      }
      
      let data;
      let responseText;
      try {
        responseText = await response.text();
        console.log('Raw response:', responseText.substring(0, 1000) + (responseText.length > 1000 ? '...' : ''));
        
        data = JSON.parse(responseText);
        console.log(`Successfully retrieved ${data.length} variations`);
        
        console.log('Variations data:', {
          count: data.length,
          variations: data.map(v => ({
            id: v.id,
            name: v.name,
            price: v.price,
            stock_status: v.stock_status,
            attributes: v.attributes
          }))
        });
        
        return data;
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', parseError);
        if (responseText) {
          console.error('Raw response preview (first 200 chars):', responseText.slice(0, 200));
        }
        throw new Error('Failed to parse variations data');
      }
      
    } catch (error) {
      console.error('Error in getProductVariations:', {
        name: error.name,
        message: error.message,
        status: error.status,
        stack: error.stack,
        response: error.response
      });
      
      // Rethrow with more context
      const enhancedError = new Error(
        `Failed to load product variations: ${error.message}`
      );
      enhancedError.originalError = error;
      throw enhancedError;
      
    } finally {
      console.groupEnd();
    }
  }
};
